import type { EvidenceType, ObservationV2 } from "./contracts.ts";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function check(value: unknown): asserts value { if (!value) throw new Error("invalid_neutral_observation"); }
function positiveRevision(value: unknown): value is string {
  return typeof value === "string" && /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n;
}
/** Identity shape only. Native locked manifest/assignment validation supplies
 * source, semantic, owner and situation authority before persistence.
 */
export function isNeutralObservation(row: ObservationV2): boolean {
  const fields = [row.source_revision, row.source_local_discriminator, row.canonical_theme_id];
  if (fields.every(value => value == null)) { check(typeof row.behavior_theme==="string"&&!row.behavior_theme.startsWith("neutral:")); return false; }
  check(positiveRevision(row.source_revision));
  check(typeof row.source_local_discriminator === "string" && UUID.test(row.source_local_discriminator));
  const pending=row.metadata.neutral_evidence_contract==='independent-evidence-v3'&&row.metadata.association_status==='pending';
  if(pending){check(row.canonical_theme_id===null&&row.behavior_theme===null&&row.metadata.polarity===null
    && !Object.prototype.hasOwnProperty.call(row.metadata,'neutral_association_contract')&&!Object.prototype.hasOwnProperty.call(row.metadata,'neutral_contrary_associations'));}
  else {
    check(typeof row.canonical_theme_id === "string" && UUID.test(row.canonical_theme_id));
    check(typeof row.behavior_theme === "string" && row.behavior_theme.trim().length > 0 && Array.from(row.behavior_theme).length <= 120);
    if (row.behavior_theme.startsWith("neutral:")) check(row.behavior_theme === `neutral:${row.canonical_theme_id}`);
  }
  check(UUID.test(row.id) && UUID.test(row.leader_id) && UUID.test(row.source_artifact_id));
  check(row.source_type === "transcript_upload" && row.evidence_type === "transcript"
    || row.source_type === "post_meeting_reflection" && row.evidence_type === "reflection");
  check(row.analysis_scope === "meeting"); return true;
}
export function observationIdentityKey(row: ObservationV2): string {
  if (isNeutralObservation(row)) return ["neutral", row.leader_id, row.source_type, row.source_artifact_id,
    row.source_revision, row.source_local_discriminator].join("|");
  return [row.leader_id, row.source_artifact_id, row.behavior_theme, row.analysis_scope, row.evidence_type].join("|");
}
function moment(row: ObservationV2): Record<string, unknown> {
  const value = row.metadata.neutral_moment;
  check(value !== null && typeof value === "object" && !Array.isArray(value)); return value as Record<string, unknown>;
}
/** Capture parent is not the experience. Unknown lineage counts zero sessions. */
export function observationSituationId(row: ObservationV2): string | null {
  if (!isNeutralObservation(row)) return row.meeting_id;
  const value = moment(row).situation;
  check(value !== null && typeof value === "object" && !Array.isArray(value));
  const situation = value as Record<string, unknown>;
  if (situation.status === "unresolved") { check(Object.keys(situation).length === 1); return null; }
  check(situation.status === "verified" && Object.keys(situation).length === 3 && typeof situation.id === "string" && UUID.test(situation.id)
    && positiveRevision(situation.lineage_revision));
  return situation.id;
}
/** Reported transcript evidence must not inherit an observed-transcript boost. */
export function observationEvidenceType(row: ObservationV2): EvidenceType {
  if (!isNeutralObservation(row)) return row.evidence_type;
  const evidence = moment(row).evidence_kind;
  check(evidence === "observed" || evidence === "reported");
  check(row.source_type !== "post_meeting_reflection" || evidence === "reported");
  return evidence === "reported" ? "reflection" : row.evidence_type;
}

export type ObservationThemeAssociation = {
  theme_id: string | null; theme_key: string; theme_revision: string | null; relation: string | null;
};
/** Shape only; native acceptance and current lineage establish authority. */
export function observationThemeAssociations(row: ObservationV2): ObservationThemeAssociation[] {
  if(row.behavior_theme===null){check(isNeutralObservation(row));return [];}
  const polarity = row.metadata.polarity;
  const marker = Object.prototype.hasOwnProperty.call(row.metadata, "neutral_association_contract");
  const field = Object.prototype.hasOwnProperty.call(row.metadata, "neutral_contrary_associations");
  // Preserve legacy endpoints for scope/readback; semantic materializers still
  // admit only exact supports/contradicts. This parser does not grant evidence.
  if (!marker && !field) return [{ theme_id: row.canonical_theme_id ?? null, theme_key: row.behavior_theme,
    theme_revision: null, relation: typeof polarity === "string" ? polarity : null }];
  check(isNeutralObservation(row) && marker && field && polarity === "supports"
    && row.metadata.neutral_association_contract === "neutral-associations-v1");
  const values = row.metadata.neutral_contrary_associations;
  check(Array.isArray(values) && values.length > 0 && values.length <= 299);
  const seen = new Set([row.canonical_theme_id]);
  const contrary = values.map(value => {
    check(value !== null && typeof value === "object" && !Array.isArray(value));
    const a = value as Record<string, unknown>;
    check(Object.keys(a).length === 4 && ["theme_id", "theme_revision", "theme_key", "relation"].every(k => Object.prototype.hasOwnProperty.call(a, k)));
    check(typeof a.theme_id === "string" && UUID.test(a.theme_id) && !seen.has(a.theme_id)); seen.add(a.theme_id);
    check(positiveRevision(a.theme_revision) && typeof a.theme_key === "string" && a.theme_key.trim().length > 0
      && Array.from(a.theme_key).length <= 120 && (a.theme_key.startsWith("neutral:") ? a.theme_key === `neutral:${a.theme_id}` : true)
      && a.relation === "contradicts");
    return a as ObservationThemeAssociation;
  });
  check(new Set([row.behavior_theme, ...contrary.map(a => a.theme_key)]).size === contrary.length + 1);
  return [{ theme_id: row.canonical_theme_id!, theme_key: row.behavior_theme, theme_revision: null, relation: "supports" }, ...contrary];
}
/** Grouping views only. Persist and deduplicate the physical row before expansion. */
export type ClassifiedObservationV2 = ObservationV2 & { behavior_theme: string };
export function isIndependentEvidence(row: ObservationV2): boolean {
  return isNeutralObservation(row) && row.behavior_theme === null;
}
export function expandObservationThemeViews(row: ObservationV2): ClassifiedObservationV2[] {
  return observationThemeAssociations(row).map((a, index) => {
    if (!index) { check(typeof row.behavior_theme === "string"); return { ...row, behavior_theme: row.behavior_theme }; }
    const { neutral_association_contract: _contract, neutral_contrary_associations: _contrary, ...metadata } = row.metadata;
    return { ...row, behavior_theme: a.theme_key, canonical_theme_id: a.theme_id,
      metadata: { ...metadata, polarity: a.relation } };
  });
}
/** Complete endpoint closure without rewriting disconnected theme components. */
export function affectedObservationThemeKeys(rows: readonly ObservationV2[], seed: readonly string[]): string[] {
  const selected = new Set(seed), associations = rows.map(row => observationThemeAssociations(row));
  let changed = true;
  while (changed) {
    changed = false;
    for (const targets of associations) if (targets.some(a => selected.has(a.theme_key))) {
      for (const a of targets) if (!selected.has(a.theme_key)) { selected.add(a.theme_key); changed = true; }
    }
  }
  return [...selected].sort();
}
